const FIREBASE_UI_ANONYMOUS_MERGE_CONFLICT =
    'firebaseui/anonymous-upgrade-merge-conflict';
const PHONE_PROVIDER_ID = 'phone';

const FIREBASE_ACCOUNT_COLLISION_CODES = new Set([
    FIREBASE_UI_ANONYMOUS_MERGE_CONFLICT,
    'auth/account-exists-with-different-credential',
    'auth/credential-already-in-use',
    'auth/email-already-in-use',
]);

const PHONE_LINK_ACCOUNT_COLLISION_CODES = new Set([
    'auth/account-exists-with-different-credential',
    'auth/credential-already-in-use',
]);

export const isFirebaseAccountCollision = (error) =>
    Boolean(error && FIREBASE_ACCOUNT_COLLISION_CODES.has(error.code));

export const getFirebaseSignInErrorMessage = (error) => {
    if (error?.code === 'auth/network-request-failed') {
        return 'Check your connection and try signing in again.';
    }

    if (error?.code === 'auth/account-exists-with-different-credential') {
        return 'This account uses a different sign-in method. Please use that method and try again.';
    }

    if (isFirebaseAccountCollision(error)) {
        return 'We couldn’t finish signing in to your existing account. Please try again.';
    }

    return 'We couldn’t sign you in. Please try again.';
};

export const handleFirebaseUiSignInFailure = async ({
    auth,
    claimRecoveryAttempt,
    error,
    onError,
    onRecoveryStart,
}) => {
    if (
        error?.code === FIREBASE_UI_ANONYMOUS_MERGE_CONFLICT &&
        error.credential?.providerId === PHONE_PROVIDER_ID
    ) {
        if (
            typeof claimRecoveryAttempt === 'function' &&
            !claimRecoveryAttempt()
        ) {
            return {
                recovered: false,
                suppressed: true,
            };
        }

        onRecoveryStart?.();

        try {
            await auth.signInWithCredential(error.credential);
            return {
                recovered: true,
            };
        } catch (recoveryError) {
            onError?.({
                error: recoveryError,
                message: getFirebaseSignInErrorMessage(recoveryError),
            });
            return {
                recovered: false,
                error: recoveryError,
            };
        }
    }

    onError?.({
        error,
        message: getFirebaseSignInErrorMessage(error),
    });
    return {
        recovered: false,
        error,
    };
};

export const installAnonymousPhoneAccountCollisionRecovery = ({
    auth,
    claimRecoveryAttempt,
    createPhoneCredential,
    onError,
    onRecoveryStart,
    user,
}) => {
    if (
        !user?.isAnonymous ||
        typeof user.linkWithPhoneNumber !== 'function' ||
        typeof createPhoneCredential !== 'function'
    ) {
        return () => {};
    }

    const originalLinkWithPhoneNumber = user.linkWithPhoneNumber;
    const recoveredLinkWithPhoneNumber = function (...args) {
        return originalLinkWithPhoneNumber
            .apply(this, args)
            .then((confirmationResult) => {
                if (
                    !confirmationResult?.verificationId ||
                    typeof confirmationResult.confirm !== 'function'
                ) {
                    return confirmationResult;
                }

                const originalConfirm = confirmationResult.confirm.bind(
                    confirmationResult,
                );

                return {
                    verificationId: confirmationResult.verificationId,
                    confirm: async (verificationCode) => {
                        try {
                            return await originalConfirm(verificationCode);
                        } catch (error) {
                            if (!PHONE_LINK_ACCOUNT_COLLISION_CODES.has(
                                error?.code,
                            )) {
                                throw error;
                            }

                            if (
                                typeof claimRecoveryAttempt === 'function' &&
                                !claimRecoveryAttempt()
                            ) {
                                throw error;
                            }

                            onRecoveryStart?.();

                            try {
                                const credential = createPhoneCredential(
                                    confirmationResult.verificationId,
                                    verificationCode,
                                );
                                if (
                                    credential?.providerId !==
                                    PHONE_PROVIDER_ID
                                ) {
                                    const invalidCredentialError = new Error(
                                        'Unable to recover the phone credential.',
                                    );
                                    invalidCredentialError.code =
                                        'auth/invalid-credential';
                                    throw invalidCredentialError;
                                }
                                return await auth.signInWithCredential(credential);
                            } catch (recoveryError) {
                                onError?.({
                                    error: recoveryError,
                                    message: getFirebaseSignInErrorMessage(
                                        recoveryError,
                                    ),
                                });
                                throw recoveryError;
                            }
                        }
                    },
                };
            });
    };

    user.linkWithPhoneNumber = recoveredLinkWithPhoneNumber;

    return () => {
        if (user.linkWithPhoneNumber === recoveredLinkWithPhoneNumber) {
            user.linkWithPhoneNumber = originalLinkWithPhoneNumber;
        }
    };
};
